import React, { FC, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { Button, ButtonProps } from '@clearcover/atlas';

import { usePlaceHelpdeskAboveElement } from 'QuoteApp/components/Helpdesk';
import { useAppContext } from 'QuoteApp/services/providers';
import { isPresent } from 'Shared/utils';

import { Rate } from './Rate';
import { RewardPointsEstimate } from './RewardPointsEstimate';
import styles from './styles.module.css';

type FooterProps = {
  nextButton?: React.ReactElement;
  previousButton?: React.ReactElement;
  hideRate?: boolean;
  showRewardsEarned?: boolean;
  disclaimer?: React.ReactElement;
  containerRef?: React.Ref<HTMLDivElement>;
  helpdeskSpacingPx?: number;
};

type FooterButtonProps = ButtonProps & {
  preserveChildren?: boolean;
};

export const FooterPreviousButton: FC<FooterButtonProps> = ({
  children,
  className,
  preserveChildren,
  ...buttonProps
}) => {
  const { isFlexPaymentPlan } = useAppContext();

  return (
    <Button
      data-testid="footer-previous-button"
      type="button"
      size="lg"
      variant="secondary"
      className={classNames(styles.previousButton, className)}
      {...buttonProps}
    >
      {isFlexPaymentPlan && !preserveChildren ? 'Back' : children}
    </Button>
  );
};

export const FooterNextButton: FC<FooterButtonProps> = ({
  children,
  className,
  preserveChildren,
  ...buttonProps
}) => {
  const { isFlexPaymentPlan } = useAppContext();

  return (
    <Button
      data-testid="footer-next-button"
      type="button"
      size="lg"
      className={classNames(styles.nextButton, className)}
      variant="primary"
      {...buttonProps}
    >
      {isFlexPaymentPlan && !preserveChildren ? 'Continue' : children}
    </Button>
  );
};

export const Footer: FC<FooterProps> = ({
  nextButton,
  previousButton,
  hideRate,
  showRewardsEarned,
  disclaimer,
  containerRef,
  helpdeskSpacingPx = -10,
}) => {
  const { isFlexPaymentPlan, rewardsEnrollmentResponse } = useAppContext();
  const isRewardsEnrolled = !!rewardsEnrollmentResponse?.isEnrolled;
  const footerWrapperRef = useRef<HTMLDivElement | null>(null);
  usePlaceHelpdeskAboveElement(footerWrapperRef, helpdeskSpacingPx);

  const setFooterWrapperRef = useCallback((element: HTMLDivElement | null) => {
    footerWrapperRef.current = element;
  }, []);

  const setFooterContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (typeof containerRef === 'function') {
        containerRef(element);
      } else if (containerRef) {
        containerRef.current = element;
      }
    },
    [containerRef]
  );

  return (
    <div ref={setFooterWrapperRef} data-quoteapp-footer="">
      <div
        ref={setFooterContainerRef}
        className={classNames(styles.footerContainer, {
          [styles.flexFooter]: isFlexPaymentPlan,
        })}
      >
        <div className={styles.content}>
          <div className={styles.left}>
            {isPresent(previousButton) && previousButton}
          </div>
          <div className={styles.center}>{!hideRate && <Rate />}</div>
          <div className={styles.right}>
            <div className={styles.pointEstimateMeter}>
              {isRewardsEnrolled && showRewardsEarned && (
                <RewardPointsEstimate />
              )}
            </div>
            {isPresent(nextButton) && nextButton}
          </div>
        </div>
      </div>
      {!!disclaimer && (
        <div className={styles.disclaimerContainer}>{disclaimer}</div>
      )}
    </div>
  );
};
